// Artifact Viewer — tabbed read-only inspector for BRD/SRD/Compliance/
// Diagrams/Wireframes/Traceability/Constraints (plan §M4.6).
//
// Tabs are lazy: each fetches its artifact body only when selected. The
// project file server (`/api/projects/:id/files/*`) returns raw bytes
// so we can render Markdown with a simple `<pre>` fallback even when
// `react-markdown` isn't wired up yet.

import { useQuery } from '@tanstack/react-query'
import { FileCode2, Image, ListTree, Network, ScrollText, Table2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { projectFileUrl } from '@/lib/api'


interface ArtifactIndexFile {
  path: string
  name: string
  size_bytes: number
  phase: number
}


interface ArtifactIndex {
  project_id: string
  run_id: number
  files: ArtifactIndexFile[]
  diagrams: ArtifactIndexFile[]
  wireframes: ArtifactIndexFile[]
  exports: ArtifactIndexFile[]
}

type TabId =
  | 'brd'
  | 'srd'
  | 'compliance'
  | 'diagrams'
  | 'wireframes'
  | 'traceability'
  | 'constraints'
  | 'audit'

interface Tab {
  id: TabId
  label: string
  icon: typeof FileCode2
  relPath: (runId: number) => string
  kind: 'markdown' | 'json' | 'csv' | 'diagrams' | 'wireframes'
}

const TABS: readonly Tab[] = [
  {
    id: 'brd',
    label: 'BRD',
    icon: ScrollText,
    relPath: (r) => `runs/run-${String(r).padStart(3, '0')}/phase3/brd.md`,
    kind: 'markdown',
  },
  {
    id: 'srd',
    label: 'SRD',
    icon: FileCode2,
    relPath: (r) => `runs/run-${String(r).padStart(3, '0')}/phase3/srd.md`,
    kind: 'markdown',
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: ScrollText,
    relPath: (r) =>
      `runs/run-${String(r).padStart(3, '0')}/phase3/compliance-risks.md`,
    kind: 'markdown',
  },
  {
    id: 'diagrams',
    label: 'Diagrams',
    icon: Network,
    relPath: (r) => `runs/run-${String(r).padStart(3, '0')}/phase5/export/diagrams`,
    kind: 'diagrams',
  },
  {
    id: 'wireframes',
    label: 'Wireframes',
    icon: Image,
    relPath: (r) => `runs/run-${String(r).padStart(3, '0')}/phase3/wireframes`,
    kind: 'wireframes',
  },
  {
    id: 'traceability',
    label: 'Traceability',
    icon: Table2,
    relPath: (r) =>
      `runs/run-${String(r).padStart(3, '0')}/phase5/export/traceability.csv`,
    kind: 'csv',
  },
  {
    id: 'constraints',
    label: 'Constraints',
    icon: ListTree,
    relPath: (r) =>
      `runs/run-${String(r).padStart(3, '0')}/phase2/constraint-log.json`,
    kind: 'json',
  },
  {
    id: 'audit',
    label: 'Audit Diff',
    icon: ScrollText,
    relPath: (r) =>
      `runs/run-${String(r).padStart(3, '0')}/phase4/audit-diff.md`,
    kind: 'markdown',
  },
]


async function fetchArtifact(
  projectId: string,
  relPath: string,
): Promise<{ ok: boolean; body: string; status: number; url: string }> {
  const url = projectFileUrl(projectId, relPath)
  const res = await fetch(url)
  const body = res.ok ? await res.text() : ''
  return { ok: res.ok, body, status: res.status, url }
}


function MarkdownPane({ body }: { body: string }) {
  // Full Markdown rendering with GFM (tables, strikethrough, task lists).
  // We strip a leading YAML frontmatter block so the viewer doesn't
  // render the document metadata as prose.
  const stripped = useMemo(() => {
    if (body.startsWith('---')) {
      const end = body.indexOf('\n---', 3)
      if (end > 0) return body.slice(end + 4).trimStart()
    }
    return body
  }, [body])

  return (
    <div
      className="prose prose-sm dark:prose-invert max-w-none rounded-md border border-border bg-background p-4
                 prose-pre:bg-muted prose-pre:text-xs prose-pre:leading-relaxed
                 prose-code:text-[0.85em] prose-table:text-xs
                 prose-headings:scroll-mt-8"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripped}</ReactMarkdown>
    </div>
  )
}


function JsonPane({ body }: { body: string }) {
  let pretty = body
  try {
    pretty = JSON.stringify(JSON.parse(body), null, 2)
  } catch {
    // Leave as-is.
  }
  return (
    <pre className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-4 text-xs leading-relaxed font-mono">
      {pretty}
    </pre>
  )
}


function CsvPane({ body }: { body: string }) {
  const rows = useMemo(() => {
    if (!body.trim()) return [] as string[][]
    const lines = body.trim().split(/\r?\n/)
    // Minimal CSV parser — assumes no embedded newlines in fields.
    return lines.map((line) => {
      const cells: string[] = []
      let cur = ''
      let inQ = false
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') {
          inQ = !inQ
        } else if (ch === ',' && !inQ) {
          cells.push(cur)
          cur = ''
        } else {
          cur += ch
        }
      }
      cells.push(cur)
      return cells
    })
  }, [body])

  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">Empty table.</p>
  }
  const [header, ...data] = rows
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="min-w-full text-xs">
        <thead className="bg-muted/40">
          <tr>
            {header.map((h, i) => (
              <th
                key={i}
                className="sticky top-0 px-3 py-2 text-left font-semibold"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-3 py-2 align-top whitespace-pre-wrap break-words max-w-md"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-border bg-muted/20 px-3 py-1.5 text-[10px] font-mono text-muted-foreground">
        {data.length} rows
      </div>
    </div>
  )
}


function useArtifactIndex(projectId: string, runId: number) {
  return useQuery<ArtifactIndex>({
    queryKey: ['artifact-index', projectId, runId],
    queryFn: async () => {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectId)}/runs/${runId}/artifacts`,
      )
      if (!res.ok) {
        throw new Error(`index fetch failed: ${res.status}`)
      }
      return (await res.json()) as ArtifactIndex
    },
    retry: false,
  })
}


function DiagramsPane({
  projectId,
  runId,
}: {
  projectId: string
  runId: number
}) {
  const index = useArtifactIndex(projectId, runId)
  // Prefer the Exporter-rendered SVGs in phase5/export/diagrams; fall back
  // to source files in phase3/diagrams when Phase 5 hasn't run yet.
  const renderedDiagrams = useMemo(() => {
    const all = index.data?.diagrams ?? []
    const svgs = all.filter(
      (f) =>
        f.path.includes('/phase5/export/diagrams/') &&
        (f.name.endsWith('.svg') || f.name.endsWith('.png')),
    )
    if (svgs.length > 0) return svgs
    return all.filter((f) => f.path.includes('/phase3/diagrams/'))
  }, [index.data])

  if (index.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading diagrams…</p>
  }
  if (renderedDiagrams.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No diagrams yet. Run Phase 3 (swarm) or Phase 5 (exporter) first.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {renderedDiagrams.length} diagram
        {renderedDiagrams.length === 1 ? '' : 's'} rendered by the Exporter
        (plan §M4.5.3). Unknown filenames 404 silently.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {renderedDiagrams.map((f) => {
          const isRenderable =
            f.name.endsWith('.svg') || f.name.endsWith('.png')
          return (
            <figure
              key={f.path}
              className="rounded-md border border-border overflow-hidden bg-white"
            >
              {isRenderable ? (
                <img
                  src={projectFileUrl(projectId, f.path)}
                  alt={f.name.replace(/[-_]/g, ' ').replace(/\.[^.]+$/, '')}
                  className="w-full h-auto"
                  loading="lazy"
                />
              ) : (
                <div className="p-3 text-[10px] font-mono text-muted-foreground">
                  {f.name} — source file (run Phase 5 to render to SVG)
                </div>
              )}
              <figcaption className="px-3 py-1.5 text-[10px] font-mono text-muted-foreground border-t border-border">
                {f.name}
              </figcaption>
            </figure>
          )
        })}
      </div>
    </div>
  )
}


function WireframesPane({
  projectId,
  runId,
}: {
  projectId: string
  runId: number
}) {
  const index = useArtifactIndex(projectId, runId)
  const wireframes = index.data?.wireframes ?? []

  if (index.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading wireframes…</p>
  }
  if (wireframes.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No wireframes yet. Run Phase 3 first (UI Sketcher).
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {wireframes.length} wireframe file{wireframes.length === 1 ? '' : 's'} produced by the UI Sketcher.
      </p>
      {wireframes.map((f) => (
        <WireframeFile
          key={f.path}
          projectId={projectId}
          url={projectFileUrl(projectId, f.path)}
          name={f.name}
        />
      ))}
    </div>
  )
}


function WireframeFile({
  projectId,
  url,
  name,
}: {
  projectId: string
  url: string
  name: string
}) {
  const q = useQuery({
    queryKey: ['wireframe', projectId, url],
    queryFn: async () => {
      const res = await fetch(url)
      return res.ok ? await res.text() : ''
    },
    retry: false,
  })
  if (!q.data) return null
  return (
    <details className="rounded-md border border-border bg-muted/40">
      <summary className="cursor-pointer px-3 py-2 text-xs font-mono">
        {name}
      </summary>
      <pre className="whitespace-pre-wrap break-words px-4 py-3 text-[11px] font-mono leading-relaxed">
        {q.data}
      </pre>
    </details>
  )
}


interface ArtifactPaneProps {
  projectId: string
  runId: number
  tab: Tab
}


function ArtifactPane({ projectId, runId, tab }: ArtifactPaneProps) {
  const relPath = tab.relPath(runId)
  const isBrowse =
    tab.kind === 'diagrams' || tab.kind === 'wireframes'
  const q = useQuery({
    queryKey: ['artifact', projectId, relPath],
    queryFn: () => fetchArtifact(projectId, relPath),
    enabled: !isBrowse,
    retry: false,
  })

  if (tab.kind === 'diagrams') {
    return <DiagramsPane projectId={projectId} runId={runId} />
  }
  if (tab.kind === 'wireframes') {
    return <WireframesPane projectId={projectId} runId={runId} />
  }

  if (q.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading…</p>
  }
  if (!q.data?.ok) {
    return (
      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          This artifact hasn't been produced yet for this run (HTTP{' '}
          {q.data?.status ?? '?'}). Run the relevant phase first.
        </p>
        <code className="block text-[10px] break-all">{relPath}</code>
      </div>
    )
  }

  switch (tab.kind) {
    case 'markdown':
      return <MarkdownPane body={q.data.body} />
    case 'json':
      return <JsonPane body={q.data.body} />
    case 'csv':
      return <CsvPane body={q.data.body} />
    default:
      return <MarkdownPane body={q.data.body} />
  }
}


export function ArtifactViewer({
  projectId,
  runId,
}: {
  projectId: string
  runId: number
}) {
  const [active, setActive] = useState<TabId>('brd')
  const activeTab = TABS.find((t) => t.id === active) ?? TABS[0]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Artifact Viewer</CardTitle>
        <CardDescription>
          Browse every artifact produced by this run (plan §M4.6).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <nav
          className="flex flex-wrap gap-2 border-b border-border pb-3"
          aria-label="Artifact tabs"
        >
          {TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <Button
                key={tab.id}
                size="sm"
                variant={tab.id === active ? 'default' : 'outline'}
                onClick={() => setActive(tab.id)}
                className={cn('gap-1')}
                aria-pressed={tab.id === active}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {tab.label}
                {tab.id === active ? (
                  <Badge variant="outline" className="ml-1 text-[10px]">
                    active
                  </Badge>
                ) : null}
              </Button>
            )
          })}
        </nav>

        <div>
          <ArtifactPane projectId={projectId} runId={runId} tab={activeTab} />
        </div>
      </CardContent>
    </Card>
  )
}
